import { useCallback, useEffect, useState } from "react";
import {
  fetchTodoistProjects,
  createTodoistProject,
  type TodoistProjectLite,
} from "../api";

export interface TodoistProjectsState {
  projects: TodoistProjectLite[];
  /** Create a new Todoist project and return it (or null on failure). The
   *  caller surfaces the error message; the list refreshes on success. */
  create: (name: string) => Promise<TodoistProjectLite | null>;
  error: string | null;
  reload: () => void;
}

/**
 * Cached Todoist project list for the My List create dropdown + badges. Empty
 * until the Stage 5 backfill populates the cache; the dropdown degrades to just
 * the "+ New project" affordance until then.
 */
export function useTodoistProjects(): TodoistProjectsState {
  const [projects, setProjects] = useState<TodoistProjectLite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchTodoistProjects()
      .then((p) => !cancelled && setProjects(p))
      .catch(() => {
        /* projects are optional; ignore until configured */
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const create = useCallback(async (name: string) => {
    setError(null);
    try {
      const created = await createTodoistProject(name);
      const lite: TodoistProjectLite = {
        id: created.id,
        name: created.name,
        color: created.color,
        is_inbox: false,
      };
      setProjects((ps) => [...ps, lite]);
      return lite;
    } catch (err) {
      setError(err instanceof Error ? err.message : "create_project_failed");
      return null;
    }
  }, []);

  return {
    projects,
    create,
    error,
    reload: () => setReloadKey((k) => k + 1),
  };
}
